export interface AttendanceRecord {
  checkIn?: string | null
  checkOut?: string | null
}

export type Phase = 'before' | 'active' | 'finished'

/**
 * Which of the three states today's attendance row is in — `FRONTEND.md` §9.32.
 *
 * Legacy's, and the whole interface the sidebar widget has: a row with no `checkIn` has not started,
 * one with a `checkIn` and no `checkOut` is running, one with both is done.
 *
 * **A missing record is `before`.** The row is created by the first punch, so *nothing recorded today*
 * and *has not arrived yet* are the same state and must not be told apart — treating a null record as
 * its own case is how a widget ends up with a fourth phase that offers nothing on the one morning it
 * matters.
 */
export function phaseOf(record: AttendanceRecord | null | undefined): Phase {
  if (!record?.checkIn) return 'before'
  return record.checkOut ? 'finished' : 'active'
}

/** `YYYY-MM-DD`, which is how the server writes a business day. */
const WORK_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Whether the sidebar's shift is yesterday's, carried over past midnight - `FRONTEND.md` §9.761, BACKEND.md §10A.415.
 *
 * Until 04:00 `GET /attendance/self/today` answers yesterday's record while it has an arrival and no departure, and
 * says so by its `workDate` falling before `today`, the business day in Bratislava. Both are compared as the
 * `YYYY-MM-DD` strings the server writes, which order as their dates do and which no zone can move.
 *
 * **A missing `today` reads as today's shift**: a server older than §10A.415 sends none, and it never carries a
 * shift over, so treating its answer as today's is what it means. A value that is not a date reads the same way,
 * because calling a shift yesterday's on the strength of something unreadable would move its departure a day.
 */
export function isCarriedOver(answer: { workDate?: unknown; today?: unknown } | null | undefined): boolean {
  const workDate = answer?.workDate
  const today = answer?.today
  if (typeof workDate !== 'string' || typeof today !== 'string') return false
  if (!WORK_DATE.test(workDate) || !WORK_DATE.test(today)) return false
  return workDate < today
}
